import { defineTour } from '../../types';

/** Every page of the console reads organisations; without it there is nothing to show. */
const VIEWS_ORGANISATIONS = 'platform.tenant.view';
/** Creating an organisation and resending its invitation (the list's Create button). */
const PROVISIONS = 'platform.tenant.provision';

/**
 * T12 — SaaS Owner operations (brief §7).
 *
 * Only in the operator console after MFA (where `GuidanceRoot` lives), with
 * `platform.tenant.view`. Its first part is the console orientation (P1, W1):
 * navigation, the platform figures and the organisations list; it works with
 * zero organisations. Create is explained only to a role that may provision,
 * as the list shows it. Its second part runs on an organisation the operator
 * opened — the guide never picks one — and pauses without one.
 *
 * The guide never creates an organisation, never resends an invitation and
 * never shows an invitation link, and it is never offered on the console's
 * security page.
 *
 * Copy: `guidance.tours.T12.*` — draft, for native-language review.
 */
const ORGANISATION = {
  routeId: 'platform.organisation',
  explainKey: 'guidance.tours.T12.openOrganisation',
} as const;

export const T12 = defineTour({
  id: 'T12',
  version: 2,
  portal: 'platform',
  titleKey: 'guidance.tours.T12.title',
  summaryKey: 'guidance.tours.T12.summary',
  eligible: (ctx) => ctx.portal === 'platform' && ctx.can(VIEWS_ORGANISATIONS),
  pageRoutes: ['platform.overview', 'platform.organisations', 'platform.organisation'],
  steps: [
    {
      id: 't12-navigation',
      // On a desk the console's navigation; below 1024px the button that opens it.
      anchor: ['platform.shell.nav', 'platform.shell.nav-phone'],
      titleKey: 'guidance.tours.T12.navigation.title',
      bodyKey: 'guidance.tours.T12.navigation.body',
    },
    {
      id: 't12-metrics',
      anchor: 'platform.overview.metrics',
      route: 'platform.overview',
      titleKey: 'guidance.tours.T12.metrics.title',
      bodyKey: 'guidance.tours.T12.metrics.body',
    },
    {
      id: 't12-definitions',
      anchor: 'platform.overview.definitions',
      titleKey: 'guidance.tours.T12.definitions.title',
      bodyKey: 'guidance.tours.T12.definitions.body',
    },
    {
      id: 't12-organisations',
      anchor: 'platform.orgs.list',
      route: 'platform.organisations',
      titleKey: 'guidance.tours.T12.organisations.title',
      bodyKey: 'guidance.tours.T12.organisations.body',
    },
    {
      // The link to the create form; highlighted only.
      id: 't12-create',
      anchor: 'platform.orgs.create',
      when: (ctx) => ctx.can(PROVISIONS),
      titleKey: 'guidance.tours.T12.create.title',
      bodyKey: 'guidance.tours.T12.create.body',
    },
    {
      // The Overview tab, where an opened organisation starts.
      id: 't12-administrator',
      anchor: 'platform.org.administrator',
      needsRecord: ORGANISATION,
      titleKey: 'guidance.tours.T12.administrator.title',
      bodyKey: 'guidance.tours.T12.administrator.body',
    },
    {
      id: 't12-sections',
      anchor: 'platform.org.tabs',
      needsRecord: ORGANISATION,
      titleKey: 'guidance.tours.T12.sections.title',
      bodyKey: 'guidance.tours.T12.sections.body',
    },
  ],
});
